/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useContext, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { map } from 'lodash';

import Dashboard from '@src/components/view/Dashboard';

import { ErrorType } from '@broadleaf/admin-components/dist/common';
import {
  ComponentRouterContext,
  ComponentRoute,
  ComponentRouteSplash
} from '@broadleaf/admin-components/dist/metadata';
import { useTenantContext } from '@broadleaf/admin-components/dist/tracking';
import { BaseLayout } from '@broadleaf/admin-components/dist/view';
import { hooks, unstable } from '@broadleaf/admin-components/dist';

const emotionCache = createCache({
  key: 'css',
  nonce: document.querySelector('meta[name="csp-nonce"]')?.['content'] || ''
});

export const AdminApp: React.FC<AdminAppProps> = ({ children }) => {
  const { routes } = useContext(ComponentRouterContext);
  const [{ applicationId, tenantId }] = useTenantContext();
  const key = `${tenantId}_${applicationId}`;

  const location = useLocation();
  const applications = hooks.useAvailableApplications();
  const actions = hooks.useTenantActions({
    queueFetchApplications: unstable.tenantActions.queueFetchApplications,
    setApplication: unstable.tenantActions.setApplication
  });

  useEffect(() => {
    if (!(applications?.length > 0)) {
      return;
    }

    const requestedApplicationId =
      new URLSearchParams(location.search).get('applicationId') || null;

    if (
      requestedApplicationId != null &&
      requestedApplicationId === applicationId
    ) {
      return;
    }

    if (requestedApplicationId) {
      for (const application of applications) {
        if (requestedApplicationId === application['id']) {
          actions.setApplication(application);
        }
      }
    }

    if (applicationId != null) {
      return;
    }

    const pathname = location.pathname || '';
    if (
      matchesPath(pathname, '/orders') ||
      matchesPath(pathname, '/pricing-requests') ||
      matchesPath(pathname, '/customers') ||
      matchesPath(pathname, '/customer-accounts')
    ) {
      actions.setApplication(applications[0]);
    }
  }, [
    actions,
    applicationId,
    applications,
    location.pathname,
    location.search
  ]);

  return (
    <CacheProvider value={emotionCache}>
      <BaseLayout>
        <Switch key={key}>
          {children}
          <Route exact path="/" component={Dashboard} />
          {map(routes, renderComponentRoute)}
          <Route>
            <ComponentRouteSplash
              isActive={true}
              error={{ type: ErrorType.Symbols.NOT_FOUND }}
            />
          </Route>
        </Switch>
      </BaseLayout>
    </CacheProvider>
  );
};

export interface AdminAppProps {
  children?: React.ReactNode;
}

function matchesPath(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function renderComponentRoute(route, index) {
  const key = route.path || index;
  return <ComponentRoute key={key} {...route} />;
}

export default AdminApp;
